import React, { ReactChild } from 'react'
import { random } from '@elgatoylacaja/utils'

type User = {
  accessToken: string
  experimentDone: boolean
  allowsNews?: boolean
  prevAllowsNews: boolean | null
}

export type AppContextData = {
  datetime: string
  auth: boolean
  visited: Array<string>
  user?: User
  experiment: Experiment
  regressors?: Regressors
  submitted: boolean
}

export type Parte1Data = {
  values: Array<{
    time: number
    option: -1 | 0 | 1
    side: -1 | 0 | 1
  }>
  porcentaje_initial: number | null
  porcentaje: number | null
  time: number
}

export type Parte2Data = {
  values: Array<{
    time: number
    image: number
    option: -1 | 0 | 1
  }>
  usedRealtimeData: boolean
  prevResults: Array<string>
  order: Array<number> // image order for user
  porcentaje_initial: number | null
  porcentaje: number | null
  porcentaje_extra_initial: number | null
  porcentaje_extra: number | null
  time: number
}

export type Parte3Data = {
  values: Array<{
    time: number
    image: number
    option: -1 | 0 | 1
  }>
  order: Array<number> // image order for user
  porcentaje_initial: number | null
  porcentaje: number | null
  porcentaje_extra_initial: number | null
  porcentaje_extra: number | null
  time: number
}

export type Experiment = {
  fork_input_slider?: 1 | 2 // 1: slider, 2: input
  fork_game_2: 3 | 6 | 9
  fork_game_3: 1 | 2 // 1: mostrando resultados, 2: sin mostrar resultados
  parte_1?: Parte1Data
  parte_2?: Parte2Data
  parte_3?: Parte3Data
  randomSides: Array<['cara' | 'ceca', number]>
}

type Regressors = {
  edad: number | string
  genero: string
  nivel_educativo: string
}

const initialAppContextData: AppContextData = {
  datetime: new Date().toLocaleString('es', { hour12: false }),
  auth: false,
  visited: [],
  experiment: {
    fork_input_slider: [1, 2][random(0)(1)] as 1 | 2,
    fork_game_2: [3, 6, 9][random(0)(2)] as 3 | 6 | 9,
    fork_game_3: [1, 2][random(0)(1)] as 1 | 2,
    randomSides: new Array(8)
      .fill((i: number) => [['cara', 'ceca'][random(0)(1)], i % 2])
      .map((f, i) => f(i)),
  },
  submitted: false,
}

type Omit<T, K extends string | number | symbol> = {
  [P in Exclude<keyof T, K>]: T[P]
}

type Login = {
  type: 'LOGIN'
  user: Omit<User, 'allowsNews'>
}

type SaveRegressors = {
  type: 'SAVE_REGRESSORS'
  regressors: Regressors
}

type SaveParte1Data = {
  type: 'SAVE_PARTE'
  parte: 'parte_1'
  values: Parte1Data['values']
  time: number
}

type Save<T extends Parte2Data | Parte3Data> = {
  type: 'SAVE_PARTE'
  parte: 'parte_2' | 'parte_3'
  values: T['values']
  time: number
  order: Array<number>
}

type SavePorcentaje = {
  type: 'SAVE_PORCENTAJE_PARTE'
  parte: 'parte_1' | 'parte_2' | 'parte_3'
  porcentaje: number
  porcentaje_initial: number
}

type SavePorcentajeExtra = {
  type: 'SAVE_PORCENTAJE_EXTRA_PARTE'
  parte: 'parte_2' | 'parte_3'
  porcentaje_extra: number
  porcentaje_extra_initial: number
  usedRealtimeData?: boolean
  prevResults?: Array<string>
}

type Visit = {
  type: 'REGISTER_VISIT'
  route: string
}

type AcceptTerms = {
  type: 'ACCEPT_TERMS'
  allowsNews: boolean
}

type SubmitData = {
  type: 'SUBMITTED_DATA'
}

type ReducerAction =
  | AcceptTerms
  | Login
  | SaveRegressors
  | SaveParte1Data
  | Save<Parte2Data>
  | Save<Parte3Data>
  | SavePorcentaje
  | SavePorcentajeExtra
  | Visit
  | SubmitData

function reducer(state: AppContextData, action: ReducerAction): AppContextData {
  switch (action.type) {
    case 'LOGIN': {
      return {
        ...state,
        auth: true,
        user: {
          ...state.user,
          ...action.user,
        },
      }
    }
    case 'SAVE_REGRESSORS': {
      return {
        ...state,
        regressors: action.regressors,
      }
    }
    case 'SAVE_PARTE': {
      return {
        ...state,
        experiment: {
          ...state.experiment,
          [action.parte]: {
            values: action.values,
            time: action.time,
            porcentaje: null,
            ...(action.parte !== 'parte_1'
              ? {
                  order: action.order,
                  porcentaje_extra: -999,
                  porcentaje_extra_initial: -999,
                }
              : {}),
          },
        },
      }
    }
    case 'SAVE_PORCENTAJE_PARTE': {
      return {
        ...state,
        experiment: {
          ...state.experiment,
          [action.parte]: {
            ...state.experiment[action.parte]!,
            porcentaje: action.porcentaje,
            porcentaje_initial: action.porcentaje_initial,
          },
        },
      }
    }
    case 'SAVE_PORCENTAJE_EXTRA_PARTE': {
      return {
        ...state,
        experiment: {
          ...state.experiment,
          [action.parte]: {
            ...state.experiment[action.parte]!,
            porcentaje_extra: action.porcentaje_extra,
            porcentaje_extra_initial: action.porcentaje_extra_initial,
            ...(action.parte === 'parte_2'
              ? {
                  usedRealtimeData: action.usedRealtimeData,
                  prevResults: action.prevResults,
                }
              : {}),
          },
        },
      }
    }
    case 'REGISTER_VISIT': {
      return {
        ...state,
        visited: [...state.visited, action.route],
      }
    }
    case 'ACCEPT_TERMS': {
      return {
        ...state,
        user: {
          ...state.user!,
          allowsNews: action.allowsNews,
        },
      }
    }
    case 'SUBMITTED_DATA': {
      return {
        ...state,
        submitted: true,
      }
    }
  }
}

export const AppContext = React.createContext({
  appState: initialAppContextData,
  dispatch: (value: ReducerAction) => {},
})

export function AppContextProvider(props: { children: ReactChild }) {
  const [state, dispatch] = React.useReducer(reducer, initialAppContextData)

  return (
    <AppContext.Provider value={{ appState: state, dispatch }}>
      {props.children}
    </AppContext.Provider>
  )
}
